/**
 * Which dashboard reads the browser may answer from memory, and for how long
 * without asking again. Everything not listed is always fetched — orders,
 * overview numbers, live sales, payments, billing, cashflow, support, access
 * grants: anything a seller expects to see the moment it changes.
 *
 * Within `freshMs` a screen reopened gets the remembered answer with no request
 * at all. After that it still gets it at once, while a refresh runs behind it
 * and the screen updates when the refresh lands. A seller's own save drops the
 * affected entries in every open tab (invalidatedBy).
 */
export interface ApiCacheRule {
  test: RegExp;
  freshMs: number;
}

export const API_CACHE_RULES: ApiCacheRule[] = [
  { test: /^\/settings(\?|$)/, freshMs: 60_000 },
  { test: /^\/products(\?|$)/, freshMs: 30_000 },
  { test: /^\/pages(\?|$)/, freshMs: 30_000 },
  { test: /^\/handles(\?|$)/, freshMs: 60_000 },
  { test: /^\/plans\/me(\?|$)/, freshMs: 60_000 },
  { test: /^\/plans(\?|$)/, freshMs: 300_000 },
  { test: /^\/admin\/platform\/plans(\?|$)/, freshMs: 60_000 },
];

export function apiCacheRule(path: string): ApiCacheRule | null {
  return API_CACHE_RULES.find((r) => r.test.test(path)) ?? null;
}

/**
 * The cached paths a successful write may have changed, as prefixes. A product
 * shows on pages and a page lists its products, so those two travel together;
 * anything touching plans or billing drops the plan reads.
 */
const DEPENDENTS: Record<string, string[]> = {
  products: ["/products", "/pages"],
  pages: ["/pages", "/products", "/handles"],
  handles: ["/handles", "/pages"],
  settings: ["/settings"],
  plans: ["/plans"],
  payments: ["/plans"],
  subscription: ["/plans"],
  "access-code": ["/plans", "/settings"],
  admin: ["/admin/platform/plans", "/plans"],
};

export function invalidatedBy(path: string): string[] {
  const first = path.replace(/^\/+/, "").split(/[/?]/)[0];
  if (first === "stores") return ["/"]; // a store change can touch any of them
  return DEPENDENTS[first] ?? [];
}

/** A cache key is `<store id>|<path>`; a prefix matches the path part. */
export function keyMatches(key: string, prefix: string): boolean {
  const path = key.slice(key.indexOf("|") + 1);
  return prefix === "/" || path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`);
}
